import React, { useEffect, useState } from 'react';

import { Fab, Tooltip, Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever
} from "@mui/icons-material";

import { 
  FeatureBase, 
  Feature,
  FeatureSource,
  DisablableOption,
} from "@models";
import { AttachedTable, CharacterFeatureSource, CharacterSheetInterface, Condition, Creature, CreatureGift, CreatureInstance, Item, MagicItemKeyword, StandardFeatures, SummonInstance } from "@dndModels";
import { FEATURE_TYPES, LEVELED_SPELL_LEVELS, RARITY_NUM_TO_COLOR_MAP, RARITY_NUM_TO_STR_MAP, RARITY_OPTIONS } from "@dndModels/Constants";
import { FeatureType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import FeatureListInput from "@dndComponents/feature/FeatureListInput";
import FeatureInput from "@dndComponents/feature/FeatureInput";
import SelectConditionBox from "@dndComponents/select/SelectConditionBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import DescriptionBox from "@dndComponents/DescriptionBox";

import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  parent_name: string;
  feature_base: FeatureBase;
  feature_bases: FeatureBase[];
  feature_source: FeatureSource | StandardFeatures | Creature | CreatureGift;
  cfs?: CharacterSheetInterface | CreatureInstance | SummonInstance | CharacterFeatureSource;
  onChange: (feature_base: FeatureBase) => void; 
  onDelete: () => void; 
  onDone: () => void;
  disabled?: boolean;
  attached_table?: AttachedTable;
  simple?: boolean;
  change_time?: number;
  children?: React.ReactNode;
  allowed_feature_types?: FeatureType[];
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function FeatureBaseInput({ 
  parent_name,
  feature_base,
  feature_bases,
  feature_source,
  cfs,
  onChange,
  onDelete,
  onDone,
  disabled = false,
  attached_table,
  simple = false,
  change_time = 0,
  children,
  allowed_feature_types = FEATURE_TYPES
}: Props) {
  const [myFB, setFB] = useState<FeatureBase>(feature_base.clone());
  const [expanded, setExpanded] = useState<Feature | null>(null);
  const [my_change_time, setChangeTime] = useState(0);

  useEffect(() => {
    if (change_time !== my_change_time) {
      // debugger;
      setChangeTime(change_time);
      setFB(feature_base.clone());
    }
  }, [change_time, my_change_time, feature_base, setChangeTime, setFB]);

  const render_replacement_key = () => {
    if (!disabled) {
      const return_us: any[] = [];
      return_us.push(
        <Grid
          key="key"
          size={{
            xs: 6
          }}>
          <StringBox
            name="Replacement Key"
            value={ myFB.replacement_key }
            disabled={disabled}
            onChange={(changed: string) => {
              const new_fb = myFB;
              new_fb.replacement_key = changed;
              setFB(new_fb);
              onChange(new_fb);
            }}
          />
        </Grid>
      );
      return_us.push(
        <Grid
          key="replace"
          size={{
            xs: 6
          }}>
          <ToggleButtonBox
            name="Replace Key"
            value={ myFB.replace_key }
            disabled={disabled}
            onToggle={() => {
              const new_fb = myFB;
              new_fb.replace_key = !new_fb.replace_key;
              setFB(new_fb);
              onChange(new_fb);
            }}
          />
        </Grid>
      );
      return return_us;
    }
    return null;
  }

  const render_other_feature_base_select = (upgrade = false) => {
    if (!disabled) {
      const options: DisablableOption[] = [{ text: "None", id: "" },...feature_bases.map(fb => {
        const opt: DisablableOption = {
          id: fb.true_id, text: `${fb.name} (Level: ${fb.level})`
        };
        return opt;
      })];
      let value = myFB.replaces_feature_base_id;
      if (upgrade) {
        value = myFB.upgrades_feature_base_id;
      }
      return (
        <SelectStringBox
          name={ upgrade ? "Upgrade Other Feature" : "Replace Other Feature" }
          options={options}
          value={ value }
          disabled={disabled}
          onChange={(changed: string) => {
            if (changed === "None") {
              changed = "";
            }
            const new_fb = myFB;
            if (upgrade) {
              new_fb.upgrades_feature_base_id = changed;
            } else {
              new_fb.replaces_feature_base_id = changed;
            }
            setFB(new_fb);
            onChange(new_fb);
          }}
        />
      );
    }
    return null;
  }

  const renderLevelSelect = () => {
    if (myFB.for_class !== "Default") {
      if (disabled) {
        return (<>Class Level: {myFB.level}</>);
      } else {
        return (
          <StringBox 
            name="Class Level"
            value={`${myFB.level}`} 
            type="number"
            onBlur={(changed: string) => {
              const new_fb = myFB;
              new_fb.level = +changed;
              setFB(new_fb);
              onChange(new_fb);
            }}
          />
        );
      }
    } else if (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) {
      if (disabled) {
        return (<>Rarity: <span style={{ color: RARITY_NUM_TO_COLOR_MAP[myFB.level]}}>{RARITY_NUM_TO_STR_MAP[myFB.level] }</span></>);
      } else {
        return (
          <SelectStringBox  
            name="Rarity"
            options={RARITY_OPTIONS.filter(o => +o.id !== -1 && +o.id >= feature_source.min_rarity && +o.id <= feature_source.max_rarity)}
            value={`${myFB.level}`}
            onChange={(changed: string) => {
              const new_fb = myFB;
              new_fb.level = +changed;
              setFB(new_fb);
              onChange(new_fb);
            }}
          />
        );
      }
    } else {
      if (disabled) {
        return (<>{ 
          (myFB.feature_source_type === "Class" || myFB.feature_source_type === "Subclass") ? 
          "Class Level" : 
          (myFB.feature_source_type === "Condition" ? "Condition Level" : "Character Level") 
        }: {myFB.level}</>);
      } else {
        return (
          <StringBox 
            name={ 
              (myFB.feature_source_type === "Class" || myFB.feature_source_type === "Subclass") ? 
              "Class Level" : 
              (myFB.feature_source_type === "Condition" ? "Condition Level" : "Character Level") 
            }
            value={`${myFB.level}`} 
            type="number"
            onBlur={(changed: string) => {
              const new_fb = myFB;
              new_fb.level = +changed;
              setFB(new_fb);
              onChange(new_fb);
            }}
          />
        );
      }
    }
  }

  const renderRequiredConditionLevelSelect = (required_condition: Condition) => {
    if (!disabled) {
      const options: string[] = [];
      for (let i = 0; i < required_condition.max_level; ++i) {
        options.push(`${i}`);
      }
      return (
        <Grid
          size={{
            xs: 2
          }}>
          <SelectStringBox  
            name="Condition Level"
            options={options}
            value={`${myFB.condition_level}`}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_fb = myFB;
              new_fb.condition_level = +changed;
              setFB(new_fb);
              onChange(new_fb);
            }}
          />
        </Grid>
      );
    } 
    return null;
  }

  const renderChildren = () => {
    if (children) {
      return (
        <Grid
          size={{
            xs: 12
          }}>{ children }</Grid>
      );
    }
    return null;
  }

  const renderConditionStuff = () => {
    if (!disabled) {
      const conditions = api.conditions;
      const required_condition = myFB.required_condition_ids.length === 1 ? conditions.find(o => o._id === myFB.required_condition_ids[0]) : undefined;
      
      return (
        <>
          <Grid
            size={{
              xs: required_condition && required_condition.max_level > 1 ? 3 : (myFB.required_condition_ids.length > 0 ? 5 : 8)
            }}>
            <SelectConditionBox
              name="Required Conditions"
              multiple
              extra_options={["All"]}
              resistances
              vulnerabilities
              immunities
              values={myFB.required_condition_ids}
              disabled={disabled}
              onChange={(ids: string[]) => {
                const new_fb = myFB;
                new_fb.required_condition_ids = ids;
                setFB(new_fb);
                onChange(new_fb);
              }}
            />
          </Grid>
          { myFB.required_condition_ids.length > 0 &&
            <Grid
              size={{
                xs: 3
              }}>
              <CheckBox 
                value={myFB.exclude_conditions} 
                name="Exclude"
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_fb = myFB;
                  new_fb.exclude_conditions = changed;
                  setFB(new_fb);
                  onChange(new_fb);
                }}
              />
            </Grid>
          }
          { required_condition && required_condition.max_level > 1 && 
            renderRequiredConditionLevelSelect(required_condition) 
          }
        </>
      );
    }
    return null;
  }

  if (simple && myFB.features.length === 1) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 6
          }}>
          { renderLevelSelect() }
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { render_other_feature_base_select(true) }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FeatureInput 
            label="Feature"
            parent_name={parent_name}
            // base_name={myFB.name}
            feature={myFB.features[0]} 
            disabled={disabled}
            attached_table={attached_table}
            cfs={cfs}
            onChange={(changed: Feature) => {
              // debugger;
              const new_fb = myFB;
              new_fb.name = changed.floating_feature?.name??"";
              new_fb.description = changed.floating_feature?.description??"";
              new_fb.features = [changed];
              setExpanded(changed);
              setFB(new_fb);
              onChange(new_fb);
            }}
            onDelete={() => {
              onDelete();
            }}
            onDone={(target: string) => {
              onDone();
            }}
          />
        </Grid>
      </Grid>
    );
  } else if (expanded) {
    return (
      <FeatureInput 
        label="Feature"
        parent_name={parent_name}
        base_name={myFB.name}
        feature={expanded} 
        disabled={disabled}
        attached_table={attached_table}
        cfs={cfs}
        allowed_feature_types={allowed_feature_types}
        onChange={(changed: Feature) => {
          const new_fb = myFB;
          new_fb.features = common_util.replaceInList(new_fb.features, changed.true_id, changed, "true_id");
          setExpanded(changed);
          setFB(new_fb);
          onChange(new_fb);
        }}
        onDelete={() => {
          const id = expanded.true_id;
          const new_fb = myFB;
          const features = new_fb.features.filter(o => o.true_id !== id);
          new_fb.features = features;
          setExpanded(null);
          setFB(new_fb);
          onChange(new_fb);
        }}
        onDone={(target: string) => {
          if (target === "base") {
            setExpanded(null);
          } else {
            setExpanded(null);
            onDone();
          }
        }}
      />
    );
  } else {
    console.log("Rendering");
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <Button
            variant="contained"
            color="primary"
            disabled={ myFB.name === "" }
            onClick={ () => { 
              onDone();
            }}>
            { parent_name === "" ? `Nameless ${myFB.feature_source_type}` : parent_name }
          </Button>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { !disabled &&
            <Grid
              size={{
                xs: 2
              }}>
              <Tooltip title={`Delete Feature Base`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  onClick={ () => {
                    onDelete();
                  }}>
                  <DeleteForever/>
                </Fab>
              </Tooltip>
            </Grid>
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { disabled ? myFB.name : 
            <StringBox 
              value={myFB.name} 
              disabled={disabled}
              name="Name"
              onBlur={(changed: string) => {
                const new_fb = myFB;
                new_fb.name = changed;
                setFB(new_fb);
                onChange(new_fb);
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { disabled ?
            <DisplayStringWithReplacements cfs={cfs} display_string={myFB.description} />
          :
            <DescriptionBox
              value={myFB.description}
              cfs={cfs}
              onChange={(changed: string) => {
                const new_fb = myFB;
                new_fb.description = changed;
                setFB(new_fb);
                onChange(new_fb);
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          { renderLevelSelect() }
        </Grid>
        { !disabled &&
          <>
            <Grid
              size={{
                xs: 3
              }}>
              <SelectObjectBox 
                name="For Class"
                value={`${myFB.for_class}`} 
                data_type="game_class"
                extra_options={["Default","Character Level"]}
                // log_it
                onChange={(changed: string) => {
                  const new_fb = myFB;
                  new_fb.for_class = changed;
                  setFB(new_fb);
                  onChange(new_fb);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <SelectStringBox
                name="Multiclassing"
                options={[
                  { text: "Unaffected", id: "0" },
                  { text: "First Class Only", id: "1" },
                  { text: "Later Classes Only", id: "2" }
                ]}
                value={`${myFB.multiclassing}`}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_fb = myFB;
                  new_fb.multiclassing = +changed;
                  setFB(new_fb);
                  onChange(new_fb);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <SelectStringBox
                name="Required Spell Slot Level"
                options={LEVELED_SPELL_LEVELS}
                extra_options={["0"]}
                value={`${myFB.min_spell_slot_level}`}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_fb = myFB;
                  new_fb.min_spell_slot_level = +changed;
                  setFB(new_fb);
                  onChange(new_fb);
                }}
              />
            </Grid>
            { renderConditionStuff() }
            <Grid
              size={{
                xs: 4
              }}>
              <CheckBox 
                value={myFB.display} 
                name="Display"
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_fb = myFB;
                  new_fb.display = changed;
                  setFB(new_fb);
                  onChange(new_fb);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              Simple Replace 
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              { render_other_feature_base_select(true) }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              Advanced Replace 
            </Grid>
            { render_replacement_key() }
            { renderChildren() }
          </>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <FeatureListInput 
            label="Feature"
            features={myFB.features} 
            parent_id={myFB.parent_id} 
            parent_type={myFB.feature_source_type}
            disabled={ disabled || myFB.name === "" || allowed_feature_types.length === 0 }
            cfs={cfs}
            onExpand={(feature: Feature) => {
              if (myFB.name !== "") {
                setExpanded(feature);
              }
            }}
            onAdd={() => {
              if (myFB.name !== "" && api.user) {
                const new_fb = myFB;
                const feature = new Feature();
                feature.parent_type = new_fb.feature_source_type;
                feature.parent_id = new_fb.feature_source_id;
                feature.base_id = new_fb.id;
                new_fb.features.push(feature);
                setFB(new_fb);
                setExpanded(feature);
                onChange(new_fb);
              }
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
